import { useEffect, useState } from 'react';
import { Badge, Button, Card, SectionHeading } from '@/components/ui';
import { Field, inputClass } from '@/components/forms/form';
import CityPicker from '@/components/forms/CityPicker';
import { AvatarUpload, GalleryUpload } from '@/components/forms/PhotoUpload';
import { useAuth } from '@/lib/auth';
import { useCities } from '@/lib/data/services';
import { supabase } from '@/lib/supabase';

/**
 * The user's own profile — the fields project.md §7 requires.
 *
 * Also the single place someone opts in to becoming a companion, as agreed in
 * the team meeting and confirmed by §25. Everyone registers as a normal user;
 * this page is where that changes. The old standalone /companion/apply route
 * still works for direct links, but nothing points at it any more — an
 * application form someone has to already know the URL of is not a funnel.
 */
type AppStatus = 'draft' | 'pending_review' | 'approved' | 'rejected' | 'suspended';
type Gender = 'female' | 'male' | 'non_binary' | 'prefer_not_to_say';

const GENDERS: { value: Gender; label: string }[] = [
  { value: 'female', label: 'Female' },
  { value: 'male', label: 'Male' },
  { value: 'non_binary', label: 'Non-binary' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
];

/** The languages that cover most of the market. Free text is added on top. */
const COMMON_LANGUAGES = [
  'English',
  'Hindi',
  'Telugu',
  'Tamil',
  'Kannada',
  'Malayalam',
  'Marathi',
  'Bengali',
  'Gujarati',
  'Punjabi',
  'Odia',
  'Urdu',
];

interface ProfileRow {
  city_id: string | null;
  gender: Gender | null;
  pincode: string | null;
  languages: string[] | null;
  is_profile_complete: boolean;
  avatar_url: string | null;
  gallery_urls: string[] | null;
  phone_e164: string | null;
}

/**
 * Indian mobile numbers, stored E.164.
 *
 * Collected but not verified — auth is email OTP, and nothing sends SMS.
 * It exists so a companion and a user have a way to reach each other around
 * an in-person booking, and it is deliberately optional: gating the profile
 * on a number nobody has verified would lose signups for no gain.
 */
const PHONE_RE = /^[6-9]\d{9}$/;

export default function Profile() {
  const { profile, refreshProfile } = useAuth();
  const cities = useCities();

  const [name, setName] = useState('');
  const [gender, setGender] = useState<Gender | ''>('');
  const [cityId, setCityId] = useState<string | null>(null);
  const [pincode, setPincode] = useState('');
  const [languages, setLanguages] = useState<string[]>([]);
  const [complete, setComplete] = useState(true);
  const [avatarKey, setAvatarKey] = useState<string | null>(null);
  const [gallery, setGallery] = useState<string[]>([]);
  const [phone, setPhone] = useState('');

  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [status, setStatus] = useState<AppStatus | null>(null);
  const [applicantMessage, setApplicantMessage] = useState<string | null>(null);
  const [loadingApp, setLoadingApp] = useState(true);

  useEffect(() => {
    if (profile) setName(profile.display_name);
  }, [profile]);

  useEffect(() => {
    let active = true;
    if (!profile) return;

    void supabase
      .from('profiles')
      .select(
        'city_id, gender, pincode, languages, is_profile_complete, ' +
          'avatar_url, gallery_urls, phone_e164',
      )
      .eq('id', profile.id)
      .maybeSingle()
      .then(({ data }) => {
        if (!active || !data) return;
        const r = data as unknown as ProfileRow;
        setCityId(r.city_id);
        setGender(r.gender ?? '');
        setPincode(r.pincode ?? '');
        setLanguages(r.languages ?? []);
        setComplete(r.is_profile_complete);
        setAvatarKey(r.avatar_url);
        setGallery(r.gallery_urls ?? []);
        // Stored E.164; shown as the ten digits people actually type.
        setPhone((r.phone_e164 ?? '').replace(/^\+91/, ''));
      });

    return () => {
      active = false;
    };
  }, [profile]);

  useEffect(() => {
    let active = true;
    if (!profile) return;

    void supabase
      .from('companion_profiles')
      .select('status, applicant_message')
      .eq('id', profile.id)
      .maybeSingle()
      .then(({ data }) => {
        if (!active) return;
        if (data) {
          setStatus(data.status as AppStatus);
          setApplicantMessage((data.applicant_message as string | null) ?? null);
        }
        setLoadingApp(false);
      });

    return () => {
      active = false;
    };
  }, [profile]);

  const citySlug = cities.find((c) => c.id === cityId)?.slug ?? null;
  const pincodeValid = pincode === '' || /^[1-9][0-9]{5}$/.test(pincode);
  const phoneValid = phone === '' || PHONE_RE.test(phone);

  function toggleLanguage(lang: string) {
    setLanguages((prev) =>
      prev.includes(lang) ? prev.filter((l) => l !== lang) : [...prev, lang],
    );
  }

  async function save() {
    if (!profile) return;
    setSaving(true);
    setError(null);

    const { error: err } = await supabase
      .from('profiles')
      .update({
        display_name: name.trim(),
        city_id: cityId,
        gender: gender || null,
        pincode: pincode.trim() || null,
        languages,
        avatar_url: avatarKey,
        gallery_urls: gallery,
        phone_e164: phone.trim() ? `+91${phone.trim()}` : null,
      })
      .eq('id', profile.id);

    if (err) {
      setError(err.message);
    } else {
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      await refreshProfile();

      // is_profile_complete is a generated column — re-read rather than
      // recompute it here, so there is one definition of "complete" and it
      // lives in the database.
      const { data } = await supabase
        .from('profiles')
        .select('is_profile_complete')
        .eq('id', profile.id)
        .maybeSingle();
      if (data) setComplete((data as { is_profile_complete: boolean }).is_profile_complete);
    }
    setSaving(false);
  }

  if (!profile) return null;

  return (
    <div className="mx-auto max-w-2xl px-4 py-10">
      <header className="animate-rise mb-8">
        <p className="eyebrow mb-2">Your account</p>
        <h1 className="text-3xl font-semibold">Profile</h1>
      </header>

      {!complete && (
        <Card className="animate-rise mb-6 px-5 py-4">
          <p className="text-sm text-[var(--ink-2)]">
            <strong className="text-[var(--ink)]">Finish your profile.</strong> Companions see who
            they are meeting before they accept a booking, so a complete profile gets accepted more
            often.
          </p>
        </Card>
      )}

      <Card className="animate-rise mb-8 p-6">
        <div className="flex flex-col gap-5">
          <div>
            <span className="mb-2 block text-sm font-medium">Profile photo</span>
            <AvatarUpload
              userId={profile.id}
              value={avatarKey}
              onChange={setAvatarKey}
              disabled={saving}
            />
          </div>

          <Field id="name" label="Name" hint="Shown to anyone you book.">
            <input
              id="name"
              className={inputClass}
              value={name}
              maxLength={60}
              onChange={(e) => setName(e.target.value)}
            />
          </Field>

          <Field
            id="phone"
            label="Phone number"
            hint="Optional. Only shared with someone you have a confirmed booking with."
            prefix="+91"
            error={phoneValid ? undefined : 'Ten digits, starting 6 to 9.'}
          >
            <input
              id="phone"
              className={inputClass}
              value={phone}
              inputMode="numeric"
              maxLength={10}
              placeholder="9876543210"
              onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
            />
          </Field>

          <div>
            <span className="mb-2 block text-sm font-medium">Gender</span>
            <div className="flex flex-wrap gap-2">
              {GENDERS.map((g) => (
                <Chip
                  key={g.value}
                  active={gender === g.value}
                  onClick={() => setGender(gender === g.value ? '' : g.value)}
                >
                  {g.label}
                </Chip>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap items-end gap-5">
            <div>
              <span className="mb-2 block text-sm font-medium">City</span>
              <CityPicker
                label=""
                value={citySlug}
                onChange={(slug) =>
                  setCityId(slug ? (cities.find((c) => c.slug === slug)?.id ?? null) : null)
                }
              />
            </div>

            <label className="text-sm">
              <span className="mb-2 block font-medium">PIN code</span>
              <input
                className="w-32 rounded-lg px-3 py-2 text-sm tabular-nums outline-none"
                style={{
                  background: 'var(--surface)',
                  color: 'var(--ink)',
                  border: `1px solid ${pincodeValid ? 'var(--line)' : 'var(--danger)'}`,
                }}
                value={pincode}
                inputMode="numeric"
                maxLength={6}
                placeholder="560001"
                onChange={(e) => setPincode(e.target.value.replace(/\D/g, ''))}
              />
            </label>
          </div>
          <p className="-mt-2 text-xs text-[var(--muted)]">
            Used to find companions near you. Only your city is ever shown publicly — never your
            PIN code or address.
          </p>

          <div>
            <span className="mb-2 block text-sm font-medium">Languages</span>
            <div className="flex flex-wrap gap-2">
              {COMMON_LANGUAGES.map((l) => (
                <Chip key={l} active={languages.includes(l)} onClick={() => toggleLanguage(l)}>
                  {l}
                </Chip>
              ))}
            </div>
          </div>

          <div>
            <span className="mb-2 block text-sm font-medium">More pictures</span>
            <GalleryUpload
              userId={profile.id}
              value={gallery}
              onChange={setGallery}
              disabled={saving}
            />
          </div>

          {error && (
            <p className="text-sm" style={{ color: 'var(--danger)' }}>
              {error}
            </p>
          )}

          <div className="flex items-center gap-3">
            <Button
              onClick={() => void save()}
              disabled={saving || !name.trim() || !pincodeValid || !phoneValid}
            >
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
            {saved && <span className="text-sm text-[var(--teal)]">Saved</span>}
          </div>
        </div>
      </Card>

      {!loadingApp && <CompanionPanel status={status} message={applicantMessage} />}
    </div>
  );
}

function Chip({
  children,
  active,
  onClick,
}: {
  children: React.ReactNode;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-full px-3 py-1.5 text-sm transition-all duration-150 hover:-translate-y-px"
      style={
        active
          ? { background: 'var(--marigold)', color: '#fff' }
          : {
              background: 'var(--surface)',
              color: 'var(--ink-2)',
              border: '1px solid var(--line)',
            }
      }
    >
      {children}
    </button>
  );
}

/**
 * The opt-in, and afterwards the application's status.
 *
 * Status is shown here rather than on a separate page because this is where
 * someone will come looking. An applicant checking "did they get back to me?"
 * should not have to find a different URL. project.md §26 requires the
 * rejection reason to be visible from the profile page specifically.
 */
function CompanionPanel({
  status,
  message,
}: {
  status: AppStatus | null;
  message: string | null;
}) {
  if (status === null) {
    return (
      <Card className="animate-rise p-6">
        <SectionHeading eyebrow="Earn on MakeMyPartner">Offer your own services</SectionHeading>
        <p className="text-[var(--ink-2)]">
          Be someone&apos;s company — for a conversation, a meal, an event, or a day out. You set
          your own prices and choose which services you offer.
        </p>
        <ul className="mt-4 mb-6 flex flex-col gap-2 text-sm text-[var(--ink-2)]">
          <li>· Verification takes 2–3 working days</li>
          <li>· You keep the majority of what you earn</li>
          <li>· Work as much or as little as you want</li>
        </ul>
        <Button to="/companion/apply">Apply to become a companion</Button>
      </Card>
    );
  }

  const view: Record<
    AppStatus,
    { tone: 'neutral' | 'marigold' | 'teal'; title: string; body: string }
  > = {
    draft: {
      tone: 'neutral',
      title: 'Application started',
      body: 'You have not submitted your application yet. Finish it whenever you are ready.',
    },
    pending_review: {
      tone: 'marigold',
      title: 'Application under review',
      body: 'Our team is checking your details. This usually takes 2–3 working days, and we will email you either way.',
    },
    approved: {
      tone: 'teal',
      title: 'You are verified',
      body: 'Set up the services you offer and start taking bookings.',
    },
    rejected: {
      tone: 'neutral',
      title: 'Application not approved',
      body: 'We could not approve your application this time.',
    },
    suspended: {
      tone: 'neutral',
      title: 'Account suspended',
      body: 'Your companion account is currently suspended. Contact support to discuss it.',
    },
  };

  const v = view[status];

  return (
    <Card className="animate-rise p-6">
      <div className="mb-3 flex items-center gap-3">
        <h2 className="text-xl font-semibold">{v.title}</h2>
        <Badge tone={v.tone}>{status.replace('_', ' ')}</Badge>
      </div>

      <p className="text-[var(--ink-2)]">{v.body}</p>

      {/* Where the admin's "we need more from you" message lands — §27. */}
      {message && (
        <div
          className="mt-4 rounded-xl px-4 py-3"
          style={{ background: 'var(--surface-2)', border: '1px solid var(--line)' }}
        >
          <p className="mb-1 text-xs font-medium tracking-wide text-[var(--muted)] uppercase">
            Message from our team
          </p>
          <p className="text-sm text-[var(--ink-2)]">{message}</p>
        </div>
      )}

      {status === 'approved' && (
        <div className="mt-6 flex flex-wrap gap-3">
          <Button to="/companion/services">Manage your services</Button>
          <Button to="/companion/availability" variant="secondary">
            Availability
          </Button>
          <Button to="/companion" variant="secondary">
            Dashboard
          </Button>
        </div>
      )}
    </Card>
  );
}
